/**
 * Per-section data form. Renders type-aware fields for the canonical
 * section types and lets the operator save / regenerate with AI.
 */
import {useEffect, useState} from 'react';
import {ApiError} from '../lib/api';
import {sitesApi} from '../lib/api-resources';
import type {
  AiGenerateResponse,
  EditorCapabilities,
  EditorMediaRef,
  EditorSection,
  EditorSectionType,
  SiteMediaRole,
} from '../lib/types';
import {friendlyError} from '../lib/error-labels';
import MediaPicker from './EditorPanels/MediaPicker';

interface Props {
  section: EditorSection;
  onChange: (next: EditorSection) => void;
  onSave: (next: EditorSection) => void;
  capabilities: EditorCapabilities;
  siteId: string;
  expectedRevision: number;
}

const KNOWN_TYPES: EditorSectionType[] = [
  'hero',
  'features',
  'about',
  'products',
  'cta',
  'faq',
];

function isRevisionConflict(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'EDITOR_REVISION_CONFLICT';
}

function isRouterUnavailable(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.code === 'AI_ROUTER_UNREACHABLE' ||
      err.code === 'AI_ROUTER_BAD_SCHEMA' ||
      err.code === 'AI_ROUTER_NOT_CONFIGURED')
  );
}

function isSchemaFailure(err: unknown): boolean {
  return (
    err instanceof ApiError &&
    (err.code === 'AI_GENERATION_SCHEMA_INVALID' ||
      err.code === 'AI_GENERATION_CONTENT_REJECTED')
  );
}

export default function SectionEditor({
  section,
  onChange,
  onSave,
  siteId,
  expectedRevision,
}: Props) {
  const [data, setData] = useState<Record<string, unknown>>(
    () => section.data || {}
  );

  const [aiState, setAiState] = useState<
    | {kind: 'idle'}
    | {kind: 'loading'}
    | {kind: 'error'; message: string; code?: string}
  >({kind: 'idle'});
  const [aiHints, setAiHints] = useState<string>('');

  useEffect(() => {
    setData(section.data || {});
    setAiState({kind: 'idle'});
    setAiHints('');
  }, [section.id]);

  const set = (path: string, value: unknown) => {
    setData((prev) => setDeep(prev, path, value));
  };

  const flush = () => {
    const next = {...section, data};
    onChange(next);
    onSave(next);
  };

  const hasExistingContent = Object.keys(data || {}).length > 0;

  const onGenerateWithAI = async () => {
    if (aiState.kind === 'loading') return;
    if (hasExistingContent) {
      const ok = window.confirm(
        'AI 将用新生成的内容覆盖当前草稿。线上站点不会受影响，但当前未保存的修改会丢失。是否继续？'
      );
      if (!ok) return;
    }
    setAiState({kind: 'loading'});
    try {
      const resp: AiGenerateResponse = await sitesApi.editor.aiGenerate(
        siteId,
        section.id,
        {prompt_hints: aiHints.trim() || null},
        expectedRevision
      );
      const nextData = (resp.section.data || {}) as Record<string, unknown>;
      setData(nextData);
      setAiState({kind: 'idle'});
      onChange({...section, data: nextData, draft_revision: resp.section.draft_revision});
    } catch (err: any) {
      let code: string | undefined;
      let message: string;
      if (err instanceof ApiError) {
        code = err.code;
        message = err.message || code;
      } else if (err instanceof Error) {
        message = err.message;
      } else {
        message = '未知错误';
      }
      setAiState({kind: 'error', message, code});
    }
  };

  if (!KNOWN_TYPES.includes(section.type as EditorSectionType)) {
    return (
      <div className="section-editor">
        <p className="warn">未知区块类型：{section.type}</p>
      </div>
    );
  }

  return (
    <div className="section-editor">
      {section.type === 'hero' && (
        <HeroFields data={data} set={set} siteId={siteId} pageId={section.page_id} sectionId={section.id} />
      )}
      {section.type === 'features' && (
        <ListFields
          data={data}
          set={set}
          itemLabel="优势"
          itemDefaults={{title: '新优势'}}
          itemFields={(path, item, setItem) => (
            <Field
              label="说明"
              value={(item.description as string) ?? ''}
              onChange={(v) => setItem(path, 'description', v || null)}
            />
          )}
        />
      )}
      {section.type === 'about' && (
        <AboutFields data={data} set={set} siteId={siteId} pageId={section.page_id} sectionId={section.id} />
      )}
      {section.type === 'products' && (
        <ProductsListFields data={data} set={set} />
      )}
      {section.type === 'cta' && (
        <CtaFields data={data} set={set} siteId={siteId} pageId={section.page_id} sectionId={section.id} />
      )}
      {section.type === 'faq' && (
        <ListFields
          data={data}
          set={set}
          itemLabel="问题"
          itemDefaults={{question: '新问题', answer: '回答'}}
          itemFields={(path, item, setItem) => (
            <Field
              label="回答"
              value={(item.answer as string) ?? ''}
              onChange={(v) => setItem(path, 'answer', v)}
              multiline
            />
          )}
        />
      )}

      <details className="raw-json">
        <summary>查看技术详情（JSON）</summary>
        <textarea
          rows={6}
          value={JSON.stringify(data, null, 2)}
          onChange={(e) => {
            try {
              setData(JSON.parse(e.target.value));
            } catch {
              // ignore parse errors so the textarea doesn't fight you
            }
          }}
        />
      </details>

      <div className="ai-generate-block">
        <div className="ai-generate-row">
          <input
            className="ai-generate-hints"
            type="text"
            placeholder="可选：给 AI 的提示（行业、语气、目标受众等）"
            value={aiHints}
            onChange={(e) => setAiHints(e.target.value)}
            disabled={aiState.kind === 'loading'}
            maxLength={500}
          />
          <button
            type="button"
            className="btn-secondary"
            onClick={onGenerateWithAI}
            disabled={aiState.kind === 'loading'}
          >
            {aiState.kind === 'loading' ? '✨ AI 生成中…' : '✨ AI 生成内容'}
          </button>
        </div>
        {aiState.kind === 'error' && (
          <div
            className={
              isRevisionConflict({code: aiState.code} as unknown)
                ? 'ai-error ai-error-conflict'
                : isRouterUnavailable({code: aiState.code} as unknown)
                ? 'ai-error ai-error-router'
                : isSchemaFailure({code: aiState.code} as unknown)
                ? 'ai-error ai-error-schema'
                : 'ai-error'
            }
          >
            {isRevisionConflict({code: aiState.code} as unknown) ? (
              <>
                <strong>{friendlyError(aiState.code).headline}</strong>{' '}
                刷新页面加载最新版本后再试一次。
              </>
            ) : isRouterUnavailable({code: aiState.code} as unknown) ? (
              <>
                <strong>{friendlyError(aiState.code).headline}</strong>{' '}
                你可以继续手动编辑当前区块。
              </>
            ) : isSchemaFailure({code: aiState.code} as unknown) ? (
              <>
                <strong>{friendlyError(aiState.code).headline}</strong>{' '}
                可以更换提示词后重新生成，或直接手动编辑下方的字段。
                <div className="ai-error-detail">{aiState.message}</div>
              </>
            ) : (
              <>
                <strong>AI 生成失败。</strong> {aiState.message}
              </>
            )}
          </div>
        )}
        {aiState.kind === 'idle' && hasExistingContent && (
          <p className="ai-generate-hint">
            再次点击会用新内容覆盖当前草稿（不影响线上站点）。
          </p>
        )}
      </div>

      <div className="section-editor-actions">
        <button className="btn-primary" onClick={flush}>
          保存
        </button>
      </div>
    </div>
  );
}

function setDeep<T>(obj: T, path: string, value: unknown): T {
  const parts = path.split('.');
  const root: Record<string, unknown> = {...(obj as Record<string, unknown>)};
  let cur: Record<string, unknown> = root;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const k = parts[i];
    if (cur[k] == null || typeof cur[k] !== 'object') {
      cur[k] = {};
    } else {
      cur[k] = {...(cur[k] as Record<string, unknown>)};
    }
    cur = cur[k] as Record<string, unknown>;
  }
  cur[parts[parts.length - 1]] = value;
  return root as T;
}

// ---- per-section field groups

function HeroFields({
  data,
  set,
  siteId,
  pageId,
  sectionId,
}: {
  data: Record<string, unknown>;
  set: (path: string, value: unknown) => void;
  siteId: string;
  pageId: string;
  sectionId: string;
}) {
  return (
    <div className="field-grid">
      <Field
        label="眉题"
        value={(data.eyebrow as string) ?? ''}
        onChange={(v) => set('eyebrow', v || null)}
      />
      <Field
        label="标题"
        value={(data.title as string) ?? ''}
        onChange={(v) => set('title', v)}
        required
      />
      <Field
        label="副标题"
        value={(data.subtitle as string) ?? ''}
        onChange={(v) => set('subtitle', v || null)}
        multiline
      />
      <FlatCtaFieldGroup
        label="主按钮"
        labelValue={(data.cta_label as string) ?? ''}
        urlValue={(data.cta_url as string) ?? ''}
        onChange={(label, url) => {
          if (label === '' && url === '') {
            set('cta_label', null);
            set('cta_url', null);
          } else {
            set('cta_label', label || null);
            set('cta_url', url || null);
          }
        }}
      />
      <FlatCtaFieldGroup
        label="次按钮"
        labelValue={(data.cta2_label as string) ?? ''}
        urlValue={(data.cta2_url as string) ?? ''}
        onChange={(label, url) => {
          if (label === '' && url === '') {
            set('cta2_label', null);
            set('cta2_url', null);
          } else {
            set('cta2_label', label || null);
            set('cta2_url', url || null);
          }
        }}
      />
      <MediaFieldGroup
        label="主图"
        media={(data.image as EditorMediaRef | null) ?? null}
        onChange={(m) => set('image', m)}
        siteId={siteId}
        role="HERO_IMAGE"
        pageId={pageId}
        sectionId={sectionId}
      />
      <Field
        label="对齐"
        value={(data.align as string) ?? 'center'}
        onChange={(v) => set('align', v)}
        options={['left', 'center', 'right']}
        optionLabels={{left: '居左', center: '居中', right: '居右'}}
      />
    </div>
  );
}

function AboutFields({
  data,
  set,
  siteId,
  pageId,
  sectionId,
}: {
  data: Record<string, unknown>;
  set: (path: string, value: unknown) => void;
  siteId: string;
  pageId: string;
  sectionId: string;
}) {
  return (
    <div className="field-grid">
      <Field
        label="标题"
        value={(data.title as string) ?? ''}
        onChange={(v) => set('title', v || null)}
      />
      <Field
        label="正文（支持简单 HTML）"
        value={(data.content as string) ?? ''}
        onChange={(v) => set('content', v)}
        multiline
        required
      />
      <MediaFieldGroup
        label="配图"
        media={(data.image as EditorMediaRef | null) ?? null}
        onChange={(m) => set('image', m)}
        siteId={siteId}
        role="ABOUT_IMAGE"
        pageId={pageId}
        sectionId={sectionId}
      />
    </div>
  );
}

function CtaFields({
  data,
  set,
  siteId,
  pageId,
  sectionId,
}: {
  data: Record<string, unknown>;
  set: (path: string, value: unknown) => void;
  siteId: string;
  pageId: string;
  sectionId: string;
}) {
  return (
    <div className="field-grid">
      <Field
        label="标题"
        value={(data.title as string) ?? ''}
        onChange={(v) => set('title', v)}
        required
      />
      <Field
        label="正文"
        value={(data.content as string) ?? ''}
        onChange={(v) => set('content', v || null)}
        multiline
      />
      <FlatCtaFieldGroup
        label="按钮"
        labelValue={(data.cta_label as string) ?? ''}
        urlValue={(data.cta_url as string) ?? ''}
        onChange={(label, url) => {
          if (label === '' && url === '') {
            set('cta_label', null);
            set('cta_url', null);
          } else {
            set('cta_label', label || null);
            set('cta_url', url || null);
          }
        }}
      />
      <MediaFieldGroup
        label="背景图"
        media={(data.background_image as EditorMediaRef | null) ?? null}
        onChange={(m) => set('background_image', m)}
        siteId={siteId}
        role="SECTION_IMAGE"
        pageId={pageId}
        sectionId={sectionId}
      />
      <Field
        label="背景配色"
        value={(data.background as string) ?? 'primary'}
        onChange={(v) => set('background', v)}
        options={['primary', 'secondary', 'accent', 'light']}
        optionLabels={{primary: '主色', secondary: '辅色', accent: '点缀色', light: '浅色'}}
      />
    </div>
  );
}

function ProductsListFields({
  data,
  set,
}: {
  data: Record<string, unknown>;
  set: (path: string, value: unknown) => void;
}) {
  const items = (data.items as Array<Record<string, unknown>>) ?? [];
  const addItem = () => {
    set('items', [...items, {title: '新产品'}]);
  };
  const removeItem = (i: number) => {
    set('items', items.filter((_, idx) => idx !== i));
  };
  const moveItem = (i: number, dir: -1 | 1) => {
    const next = [...items];
    const target = i + dir;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target], next[i]];
    set('items', next);
  };
  const setItem = (path: string, key: string, value: unknown) => {
    const i = Number(path);
    const next = items.map((it, idx) =>
      idx === i ? {...it, [key]: value} : it
    );
    set('items', next);
  };
  return (
    <div className="list-fields">
      <Field
        label="区块标题"
        value={(data.title as string) ?? ''}
        onChange={(v) => set('title', v || null)}
      />
      <Field
        label="副标题"
        value={(data.subtitle as string) ?? ''}
        onChange={(v) => set('subtitle', v || null)}
        multiline
      />
      <ol className="list-items">
        {items.map((it, i) => (
          <li key={i}>
            <div className="list-item-head">
              <strong>产品 {i + 1}</strong>
              <button className="icon-btn" onClick={() => moveItem(i, -1)} disabled={i === 0} title="上移">↑</button>
              <button className="icon-btn" onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} title="下移">↓</button>
              <button className="icon-btn danger" onClick={() => removeItem(i)} title="删除">×</button>
            </div>
            <Field
              label="产品名"
              value={(it.title as string) ?? ''}
              onChange={(v) => setItem(String(i), 'title', v)}
              required
            />
            <Field
              label="产品说明"
              value={(it.description as string) ?? ''}
              onChange={(v) => setItem(String(i), 'description', v || null)}
            />
            <Field
              label="链接地址"
              value={(it.link_url as string) ?? ''}
              onChange={(v) => setItem(String(i), 'link_url', v || null)}
            />
            <Field
              label="链接文字"
              value={(it.link_label as string) ?? ''}
              onChange={(v) => setItem(String(i), 'link_label', v || null)}
            />
          </li>
        ))}
      </ol>
      <button className="btn-secondary" onClick={addItem}>
        + 添加产品
      </button>
    </div>
  );
}

interface ListFieldsProps {
  data: Record<string, unknown>;
  set: (path: string, value: unknown) => void;
  itemLabel: string;
  itemDefaults: Record<string, unknown>;
  headingLabel?: string;
  showSubtitle?: boolean;
  itemFields?: (
    path: string,
    item: Record<string, unknown>,
    setItem: (path: string, key: string, value: unknown) => void
  ) => React.ReactNode;
}

function ListFields({
  data,
  set,
  itemLabel,
  itemDefaults,
  headingLabel = '区块标题',
  showSubtitle = true,
  itemFields,
}: ListFieldsProps) {
  const items = (data.items as Array<Record<string, unknown>>) ?? [];
  const addItem = () => {
    set('items', [...items, {...itemDefaults}]);
  };
  const removeItem = (i: number) => {
    set('items', items.filter((_, idx) => idx !== i));
  };
  const moveItem = (i: number, dir: -1 | 1) => {
    const next = [...items];
    const target = i + dir;
    if (target < 0 || target >= next.length) return;
    [next[i], next[target]] = [next[target], next[i]];
    set('items', next);
  };
  const setItem = (path: string, key: string, value: unknown) => {
    const i = Number(path);
    const next = items.map((it, idx) =>
      idx === i ? {...it, [key]: value} : it
    );
    set('items', next);
  };
  return (
    <div className="list-fields">
      <Field
        label={headingLabel}
        value={(data.title as string) ?? ''}
        onChange={(v) => set('title', v || null)}
      />
      {showSubtitle && (
        <Field
          label="副标题"
          value={(data.subtitle as string) ?? ''}
          onChange={(v) => set('subtitle', v || null)}
          multiline
        />
      )}
      <ol className="list-items">
        {items.map((it, i) => (
          <li key={i}>
            <div className="list-item-head">
              <strong>{itemLabel} {i + 1}</strong>
              <button className="icon-btn" onClick={() => moveItem(i, -1)} disabled={i === 0} title="上移">↑</button>
              <button className="icon-btn" onClick={() => moveItem(i, 1)} disabled={i === items.length - 1} title="下移">↓</button>
              <button className="icon-btn danger" onClick={() => removeItem(i)} title="删除">×</button>
            </div>
            <Field
              label={itemLabel}
              value={(it.title as string) ?? (it.question as string) ?? ''}
              onChange={(v) => setItem(String(i), itemLabel === '问题' ? 'question' : 'title', v)}
              required
            />
            {itemFields && itemFields(String(i), it, setItem)}
          </li>
        ))}
      </ol>
      <button className="btn-secondary" onClick={addItem}>
        + 添加{itemLabel}
      </button>
    </div>
  );
}

// ---- reusable widgets

function Field({
  label,
  value,
  onChange,
  required,
  multiline,
  options,
  optionLabels,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  required?: boolean;
  multiline?: boolean;
  options?: string[];
  optionLabels?: Record<string, string>;
}) {
  return (
    <label className="field">
      <span className="field-label">
        {label}
        {required && <em className="required">*</em>}
      </span>
      {options ? (
        <select value={value} onChange={(e) => onChange(e.target.value)}>
          {options.map((o) => (
            <option key={o} value={o}>
              {optionLabels?.[o] ?? o}
            </option>
          ))}
        </select>
      ) : multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          rows={3}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </label>
  );
}

function FlatCtaFieldGroup({
  label,
  labelValue,
  urlValue,
  onChange,
}: {
  label: string;
  labelValue: string;
  urlValue: string;
  onChange: (label: string, url: string) => void;
}) {
  return (
    <fieldset className="field-group">
      <legend>{label}</legend>
      <label className="field">
        <span className="field-label">按钮文字</span>
        <input
          type="text"
          value={labelValue}
          onChange={(e) => onChange(e.target.value, urlValue)}
        />
      </label>
      <label className="field">
        <span className="field-label">链接地址</span>
        <input
          type="text"
          value={urlValue}
          onChange={(e) => onChange(labelValue, e.target.value)}
        />
      </label>
      {(labelValue || urlValue) && (
        <button
          type="button"
          className="btn-link"
          onClick={() => onChange('', '')}
        >
          移除
        </button>
      )}
    </fieldset>
  );
}

function MediaFieldGroup({
  label,
  media,
  onChange,
  siteId,
  role,
  pageId,
  sectionId,
}: {
  label: string;
  media: EditorMediaRef | null;
  onChange: (m: EditorMediaRef | null) => void;
  siteId: string;
  role: SiteMediaRole;
  pageId: string | null;
  sectionId: string | null;
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  return (
    <fieldset className="field-group">
      <legend>{label}</legend>
      {media ? (
        <div className="media-preview">
          <AttachmentThumb attachmentId={media.attachment_id} alt={media.alt || ''} siteId={siteId} />
          <span className="media-meta">#{media.attachment_id}</span>
          <button
            type="button"
            className="btn-link"
            onClick={() => setPickerOpen(true)}
          >
            替换
          </button>
          <button
            type="button"
            className="btn-link"
            onClick={() => onChange(null)}
          >
            移除
          </button>
        </div>
      ) : (
        <button
          type="button"
          className="btn-secondary"
          onClick={() => setPickerOpen(true)}
        >
          + 选择{label}
        </button>
      )}
      {media && (
        <label className="field">
          <span className="field-label">替代文字（无障碍 / SEO）</span>
          <input
            type="text"
            value={media.alt || ''}
            onChange={(e) =>
              onChange({...media, alt: e.target.value || null})
            }
          />
        </label>
      )}
      {pickerOpen && (
        <MediaPicker
          siteId={siteId}
          role={role}
          pageId={pageId}
          sectionId={sectionId}
          onSelect={(m) => {
            if (m.attachment_id === -1) {
              window.dispatchEvent(new CustomEvent('wujiong:section-reload-needed', {
                detail: {sectionId},
              }));
              setPickerOpen(false);
              return;
            }
            onChange({
              attachment_id: m.attachment_id,
              alt: media?.alt ?? m.alt ?? null,
            });
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
          initialAttachmentId={media?.attachment_id && media.attachment_id > 0 ? media.attachment_id : null}
          title={`选择${label}`}
        />
      )}
    </fieldset>
  );
}

function AttachmentThumb({attachmentId, alt, siteId}: {attachmentId: number; alt: string; siteId: string}) {
  // Use a stable API endpoint to render the thumb. For attachment_id < 0
  // (sentinel pre-publish), the editor will re-render after the next
  // reload; here we just hide the thumb rather than 404.
  if (attachmentId < 0) {
    return <div className="media-thumb media-thumb-pending">（图片上传中，刷新后显示）</div>;
  }
  const url = `/api/v1/sites/${siteId}/media/${attachmentId}?size=thumb`;
  return <img className="media-thumb" src={url} alt={alt} />;
}
